import { WorkPauseBlock, Service } from '@features/service/entity';
import { WorkingHoursScheduleType } from '@features/visits/schedule/entity';
import { axiosInstance } from '@libs/axios/axios';
import { customerAxiosInstance } from '@libs/axios/customerAxiosInstance';
import { apiCall } from '@shared/entity';
import { CreateCustomerVisitData } from './queries';

export const clientAuthApi = {
  createActivationToken: (clientId: string) =>
    `/api/clients/${encodeURIComponent(clientId)}/activate`,
  revokeReservationAccess: (clientId: string) =>
    `/api/clients/${encodeURIComponent(clientId)}/revoke-access`,
  activate: (token: string) =>
    `/api/customer-reservation/activate?token=${encodeURIComponent(token)}`,
  validate: () => '/api/customer-reservation/validate',
  login: () => '/api/customer-reservation/login',
  loginLookup: (phone: string) =>
    `/api/customer-reservation/login-lookup?phone=${encodeURIComponent(phone)}`,
  createDeviceLink: () => '/api/device-link/create',
  confirmDeviceLink: () => '/api/device-link/confirm',
  reservationServices: () => `/api/customer-reservation/reservation-services`,
  hairdresserSchedule: () => `/api/customer-reservation/hairdresser-schedule`,
  visits: () => '/api/customer-reservation/visits',
  createVisit: () => '/api/customer-reservation/create-visit',
  myVisits: () => `/api/customer-reservation/my-visits`,
};

export type ActivationTokenResponse = {
  token: string;
  expiresAt: string;
  activationUrl: string;
};

export type ActivateClientResponse = {
  success: boolean;
  sessionToken: string;
  clientId: string;
  expiresAt: string;
};

export type ValidateSessionResponse = {
  valid: boolean;
  clientId: string | null;
  userId?: string | null;
  loyaltyPoints?: number;
  sessionToken?: string;
  hasPinSet?: boolean;
};

export type DeviceLinkTokenResponse = {
  linkToken: string;
  expiresAt: string;
  confirmationUrl: string;
};

export type ConfirmDeviceLinkResponse = {
  success: boolean;
  sessionToken: string;
  clientId: string;
  expiresAt: string;
};

export type ReservationsVisits = {
  visitId: string;
  date: Date;
  dateTo: Date;
  blocks: WorkPauseBlock[];
};

export type ServiceType = {
  id: string;
  serviceName: string;
  duration: number;
  price: number;
  color?: string;
  userId: string;
};

export type ReservationServicesResponse = {
  services: Service[];
  clientId: string;
  clientName: string;
};

export type HairdresserScheduleResponse = {
  userId: string;
  workingSchedule: WorkingHoursScheduleType | null;
  isScheduleConfigured: boolean;
};

export type ClientVisit = {
  id: string;
  date: string;
  dateTo: string;
  isOwnVisit: boolean;
};

export type MyVisit = {
  id: string;
  date: string;
  dateTo: string;
  serviceName: string;
  serviceColor?: string;
  hairdresserName?: string;
  services: { name: string }[];
};

export type MyVisitsResponse = MyVisit[];

export type LoginWithPinResponse = {
  success: boolean;
  clientId: string;
};

export type LoginLookupEntry = {
  userId: string;
  hairdresserName: string;
};

export const createActivationToken = async (clientId: string): Promise<ActivationTokenResponse> =>
  apiCall(
    async () => await axiosInstance.post(clientAuthApi.createActivationToken(clientId)),
    'api.customerReservations.activationTokenError',
  );

export const revokeReservationAccess = async (clientId: string): Promise<void> =>
  apiCall(
    async () => await axiosInstance.delete(clientAuthApi.revokeReservationAccess(clientId)),
    'api.customerReservations.revokeAccessError',
  );

export const activateClient = async (token: string): Promise<ActivateClientResponse> =>
  apiCall(
    async () => await customerAxiosInstance.get(clientAuthApi.activate(token)),
    'api.customerReservations.activationError',
  );

export const validateSession = async (): Promise<ValidateSessionResponse> =>
  apiCall(
    async () => await customerAxiosInstance.post(clientAuthApi.validate()),
    'api.customerReservations.sessionVerifyError',
  );

export const logoutSession = async (): Promise<void> => {
  await customerAxiosInstance.post('/api/customer-reservation/logout').catch(() => {});
};

export const createDeviceLink = async (): Promise<DeviceLinkTokenResponse> =>
  apiCall(
    async () => await customerAxiosInstance.post(clientAuthApi.createDeviceLink()),
    'api.customerReservations.deviceLinkError',
  );

export const confirmDeviceLink = async (linkToken: string): Promise<ConfirmDeviceLinkResponse> =>
  apiCall(
    async () => await customerAxiosInstance.post(clientAuthApi.confirmDeviceLink(), { linkToken }),
    'api.customerReservations.deviceConfirmError',
  );

export const getReservationServices = async (): Promise<ReservationServicesResponse> =>
  apiCall(
    async () => await customerAxiosInstance.get(clientAuthApi.reservationServices()),
    'api.customerReservations.servicesNotFound',
  );

export const getHairdresserSchedule = async (
  year?: number,
  month?: number,
): Promise<HairdresserScheduleResponse> =>
  apiCall(
    async () =>
      await customerAxiosInstance.get(clientAuthApi.hairdresserSchedule(), {
        params: year && month ? { year, month } : undefined,
      }),
    'api.customerReservations.scheduleNotFound',
  );

export const getCustomerVisits = async (
  from: string,
  to: string,
  userId: string,
): Promise<ReservationsVisits[]> =>
  apiCall(
    async () =>
      await customerAxiosInstance.get(clientAuthApi.visits(), {
        params: { from, to, userId },
      }),
    'api.customerReservations.visitsNotFound',
  );

export type CreateCustomerVisitResponse = {
  id: string;
  date: Date;
  dateTo: Date;
  clientId: string;
};

export const createCustomerVisit = async (
  visitData: CreateCustomerVisitData,
): Promise<CreateCustomerVisitResponse> =>
  apiCall(
    async () => await customerAxiosInstance.post(clientAuthApi.createVisit(), visitData),
    'api.customerReservations.createError',
  );

export const getMyVisits = async (): Promise<MyVisitsResponse> =>
  apiCall(
    async () => await customerAxiosInstance.get(clientAuthApi.myVisits()),
    'api.customerReservations.myVisitsNotFound',
  );

export const loginWithPin = async (params: {
  phone: string;
  userId: string;
  pin: string;
}): Promise<LoginWithPinResponse> =>
  apiCall(
    async () => await customerAxiosInstance.post(clientAuthApi.login(), params),
    'api.customerReservations.loginError',
  );

export const loginLookup = async (phone: string): Promise<LoginLookupEntry[]> =>
  apiCall(
    async () => await customerAxiosInstance.get(clientAuthApi.loginLookup(phone)),
    'api.customerReservations.loginError',
  );

export const setClientPinSelf = async (params: {
  pin: string;
  email: string;
}): Promise<{ success: boolean }> =>
  apiCall(
    async () => await customerAxiosInstance.post('/api/customer-reservation/set-pin', params),
    'api.customerReservations.setPinError',
  );

export const requestPinReset = async (params: {
  phone: string;
  userId: string;
}): Promise<{ success: boolean; message: string }> =>
  apiCall(
    async () => await customerAxiosInstance.post('/api/customer-reservation/forgot-pin', params),
    'api.customerReservations.resetPinRequestError',
  );

export const resetClientPin = async (params: {
  token: string;
  pin: string;
}): Promise<{ success: boolean }> =>
  apiCall(
    async () => await customerAxiosInstance.post('/api/customer-reservation/reset-pin', params),
    'api.customerReservations.resetPinError',
  );
